import { SearchContainer, Container, FilterContainer } from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import TableHeader from "./table-header";
import InviteeItem from "./invitee-item";
import AlertModal from "uis/modal/alert-modal";
import { useCallback, useEffect, useState } from "react";
import { roleToString, UserRole } from "utils/role";
import {
  cancelInviteMember,
  getInviteMember,
  resendInviteMember,
  TInviteMember,
} from "apis/member-invites/member-invites";
import EmptyRow from "./empty-row";
import { debounce } from "lodash";
import { useToast } from "hook/useToast";

const InviteMember = ({
  open,
  update,
  setUpdateInvite,
}: {
  open: boolean;
  update: boolean;
  setUpdateInvite: (value: boolean) => void;
}) => {
  const { showToast } = useToast();

  const [totalPage, setTotalPage] = useState<number>(1);
  const [page, setPage] = useState<number>(1);
  const [members, setMembers] = useState<TInviteMember[]>([]);
  const [searchRole, setSearchRole] = useState<number>();
  const [searchMember, setSearchMember] = useState<string>();

  const [clickItem, setClickItem] = useState<TInviteMember>();
  const [reSendModal, setReSendModal] = useState<boolean>(false);
  const [cancelModal, setCancelModal] = useState<boolean>(false);

  const onHandleResend = async () => {
    const res = await resendInviteMember(clickItem?.id ?? "");
    if (res.success) {
      showToast("초대가 재전송되었습니다.", "success");
      getMemberListByFilter(page, searchRole, searchMember);
    } else {
      showToast("초대 재전송이 실패되었습니다. 잠시 후 다시 시도해주세요.", "error");
    }
    setReSendModal(false);
  };

  const onHandleCancel = async () => {
    const res = await cancelInviteMember(clickItem?.id ?? "");
    if (res.success) {
      showToast("초대가 취소되었습니다.", "success");
      getMemberListByFilter(page, searchRole, searchMember);
    } else {
      showToast("초대 취소가 실패되었습니다. 잠시 후 다시 시도해주세요.", "error");
    }
    setCancelModal(false);
  };

  const onHandleDeleteFilter = () => {
    setSearchRole(undefined);
    getMemberListByFilter(1, undefined, searchMember);
  };

  const getMemberListByFilter = async (page: number, searchRole?: number, searchMember?: string) => {
    const res = await getInviteMember(page, searchRole, searchMember);
    if (res) {
      setTotalPage(res.totalPageNumber ?? 1);
      setMembers(res.memberInviteeList ?? []);
      setPage(res?.nowPageNumber ?? 1);
    }
  };

  useEffect(() => {
    if (open) getMemberListByFilter(1, searchRole, searchMember);
  }, [open]);

  useEffect(() => {
    if (update) {
      getMemberListByFilter(1, searchRole, searchMember);
      setUpdateInvite(false);
    }
  }, [update]);

  return (
    <Container style={{ display: open ? "flex" : "none" }}>
      <SearchContainer>
        <FilterContainer>
          {searchRole !== undefined && (
            <UI.Chip.Contained
              size="medium"
              rightIcon="x"
              title={`권한: ${roleToString(searchRole)}`}
              showRightIcon
              onClick={onHandleDeleteFilter}
            />
          )}
        </FilterContainer>
        <UI.Input
          placeholder="멤버를 검색해 주세요."
          onChange={debounce((value) => {
            getMemberListByFilter(1, searchRole, value);
            setSearchMember(value);
          }, 500)}
          prefixIcon="search"
          showPrefixIcon
          width={280}
        />
      </SearchContainer>
      <TableHeader
        selectedRole={searchRole}
        onChangeRole={(role) => {
          setSearchRole(role);
          getMemberListByFilter(1, role, searchMember);
        }}
      />
      {members.map((item, index) => (
        <InviteeItem
          key={item.email}
          email={item.email}
          role={item.role}
          state={new Date(item.expiredAt).getTime() <= new Date().getTime() ? "expried" : "wait"}
          onHandleResend={() => {
            setClickItem(item);
            setReSendModal(true);
          }}
          onHandleDelete={() => {
            setClickItem(item);
            setCancelModal(true);
          }}
        />
      ))}
      {members.length === 0 ? <EmptyRow /> : <></>}
      <div style={{ marginTop: "48px" }}>
        {totalPage !== 0 && (
          <UI.Pagination
            pageCount={totalPage}
            currentPage={page}
            onChange={(page: number) => {
              if (page === 0) return;
              getMemberListByFilter(page, searchRole, searchMember);
            }}
          />
        )}
      </div>
      <AlertModal
        title="초대를 재전송하시겠습니까?"
        content={
          <p style={{ textAlign: "center" }}>
            해당 이메일로 초대 링크가 재전송됩니다.
            <br />
            이미 전송된 링크가 있다면 만료 처리됩니다.
          </p>
        }
        btnTitle="초대 재전송하기"
        modalIsOpen={reSendModal}
        closeModal={() => setReSendModal(false)}
        onChange={onHandleResend}
      />
      <AlertModal
        title="초대를 취소하시겠습니까?"
        content={
          <p style={{ textAlign: "center" }}>
            해당 이메일로 발송된 초대 링크가 만료되며,
            <br />
            초대 현황 리스트에서도 사라집니다.
          </p>
        }
        btnTitle="초대 취소하기"
        modalIsOpen={cancelModal}
        closeModal={() => setCancelModal(false)}
        onChange={onHandleCancel}
      />
    </Container>
  );
};

export default InviteMember;
